package com.arena_web.ui.components.modal

import androidx.compose.runtime.Composable
import com.arena_web.ui.components.cards.BookingField
import com.arena_web.ui.components.cards.PaymentStatus
import com.arena_web.ui.components.widgets.ModalBookingStatusBadge
import com.arena_web.ui.components.widgets.ModalFieldBadge
import com.arena_web.ui.components.widgets.ModalPaymentStatusBadge
import com.arena_web.ui.localization.LocalAppStrings
import com.arena_web.ui.theme.*
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.*

// =============================================================================
// Modal action variants
// =============================================================================

enum class ModalAction {
    /** View-only modal with "Close" button */
    Action,
    /** Modal with "Decline" + "Confirm" buttons */
    Approval,
    /** Modal with "Complete Session" button */
    Complete
}

// =============================================================================
// BookingDetailModal
// =============================================================================

@Composable
fun BookingDetailModal(
    action: ModalAction = ModalAction.Action,
    customerName: String = "Michael Anderson",
    subtitle: String = "Today",
    field: BookingField = BookingField.Field01,
    bookingStatus: String = "Completed",
    paymentStatus: PaymentStatus = PaymentStatus.Unpaid,
    timeFrom: String = "10:00",
    timeTo: String = "11:00",
    price: String = "100",
    avatarUrl: String? = null,
    onClose: () -> Unit = {},
    onDecline: () -> Unit = {},
    onConfirm: () -> Unit = {},
    onCompleteSession: () -> Unit = {},
    modifier: Modifier = Modifier
) {
    val t = LocalAppStrings.current
    Column(
        modifier
            .sBorderRadius(12)
            .backgroundColor(BusinessColors.Modal.Background)
            .styleModifier {
                property("overflow", "clip")
                when (action) {
                    ModalAction.Action -> property("padding", s(12))
                    ModalAction.Approval, ModalAction.Complete ->
                        property("padding", "${s(12)} ${s(12)} ${s(18)} ${s(12)}")
                }
            },
        horizontalAlignment = if (action == ModalAction.Complete) Alignment.CenterHorizontally else Alignment.Start
    ) {
        // Main content container
        Column(
            Modifier.styleModifier {
                if (action != ModalAction.Complete) property("gap", s(28))
            }
        ) {
            // Top section: user info + booking details
            Column(
                Modifier.styleModifier { property("gap", s(22)) }
            ) {
                // User info row
                UserInfoRow(customerName, subtitle, avatarUrl)

                // Booking details
                BookingDetailsSection(field, bookingStatus, paymentStatus, timeFrom, timeTo, price)
            }

            // Action button(s)
            when (action) {
                ModalAction.Action -> {
                    // Close button
                    Box(
                        Modifier
                            .sBorderRadius(28)
                            .styleModifier {
                                property("padding", "${s(8)} ${s(32)}")
                                property("cursor", "pointer")
                            }
                            .onClick { onClose() },
                        contentAlignment = Alignment.Center
                    ) {
                        SpanText(
                            t.commonClose,
                            modifier = Modifier.bodyLMedium().color(BusinessColors.Modal.TextButton)
                        )
                    }
                }
                ModalAction.Approval -> {
                    // Decline + Confirm buttons
                    Row(
                        Modifier.styleModifier { property("gap", s(8)) }
                    ) {
                        // Decline button
                        Row(
                            Modifier
                                .sBorderRadius(22)
                                .styleModifier {
                                    property("border", "0.3px solid rgba(255, 255, 255, 0.4)")
                                    property(
                                        "background",
                                        AppGradients.InteractiveSecondary
                                    )
                                    property("padding", "${s(8)} ${s(16)}")
                                    property("cursor", "pointer")
                                    property("gap", s(8))
                                }
                                .onClick { onDecline() },
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Image(
                                src = AppIcons.ModalClose,
                                modifier = Modifier.sSize(16),
                                alt = t.modalDecline
                            )
                            SpanText(
                                t.modalDecline,
                                modifier = Modifier.bodyLMedium().color(BusinessColors.Text.OnSurfaceMuted)
                            )
                        }

                        // Confirm button
                        Row(
                            Modifier
                                .sBorderRadius(22)
                                .backgroundColor(AppColors.Primary)
                                .styleModifier {
                                    property("padding", "${s(8)} ${s(16)}")
                                    property("cursor", "pointer")
                                    property("gap", s(4))
                                }
                                .onClick { onConfirm() },
                            verticalAlignment = Alignment.CenterVertically
                        ) {
                            Image(
                                src = AppIcons.ModalCheck,
                                modifier = Modifier.sSize(16),
                                alt = t.commonConfirm
                            )
                            SpanText(
                                t.commonConfirm,
                                modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite)
                            )
                        }
                    }
                }
                ModalAction.Complete -> { /* Complete Session button below */ }
            }
        }

        // Complete Session button (separate from main content, with 28px gap)
        if (action == ModalAction.Complete) {
            Box(Modifier.sHeight(28)) // spacer
            Row(
                Modifier
                    .sBorderRadius(22)
                    .backgroundColor(AppColors.Primary)
                    .styleModifier {
                        property("padding", "${s(8)} ${s(16)}")
                        property("cursor", "pointer")
                        property("gap", s(4))
                    }
                    .onClick { onCompleteSession() },
                verticalAlignment = Alignment.CenterVertically
            ) {
                Image(
                    src = AppIcons.ModalCheckCircle,
                    modifier = Modifier.sSize(16),
                    alt = t.modalCompleteSession
                )
                SpanText(
                    t.modalCompleteSession,
                    modifier = Modifier.bodyLMedium().color(AppColors.PrimaryWhite)
                )
            }
        }
    }
}

// =============================================================================
// Sub-components
// =============================================================================

@Composable
private fun UserInfoRow(
    customerName: String,
    subtitle: String,
    avatarUrl: String?
) {
    Row(
        Modifier.styleModifier { property("gap", s(6)) },
        verticalAlignment = Alignment.CenterVertically
    ) {
        // Avatar
        Box(
            Modifier
                .sSize(36)
                .sBorderRadius(100)
                .backgroundColor(AppColors.Text.Primary)
                .styleModifier { property("overflow", "clip") }
        ) {
            avatarUrl?.let {
                Image(
                    src = it,
                    modifier = Modifier.fillMaxSize().styleModifier {
                        property("object-fit", "cover")
                    },
                    alt = customerName
                )
            }
        }

        // Name + subtitle
        Column(
            Modifier.sWidth(129).styleModifier { property("gap", s(2)) }
        ) {
            SpanText(
                customerName,
                modifier = Modifier.bodyLSemiBold().color(AppColors.Text.Secondary)
            )
            SpanText(
                subtitle,
                modifier = Modifier.bodyMRegular().color(BusinessColors.Text.OnSurfaceMuted)
            )
        }
    }
}

@Composable
private fun BookingDetailsSection(
    field: BookingField,
    bookingStatus: String,
    paymentStatus: PaymentStatus,
    timeFrom: String,
    timeTo: String,
    price: String
) {
    val t = LocalAppStrings.current
    Column(
        Modifier.styleModifier { property("gap", s(16)) }
    ) {
        // Status badges row
        Row(
            Modifier.styleModifier { property("gap", s(8)) },
            verticalAlignment = Alignment.CenterVertically
        ) {
            ModalFieldBadge(field = field)
            ModalBookingStatusBadge(status = bookingStatus)
            ModalPaymentStatusBadge(paymentStatus = paymentStatus)
        }

        // Time and payment info row
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier.styleModifier { property("gap", s(14)) }
        ) {
            // Time range
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.styleModifier { property("gap", s(2)) }
            ) {
                Image(
                    src = AppIcons.ModalTime,
                    modifier = Modifier.sSize(14),
                    alt = t.modalTimeAlt
                )
                Row(
                    modifier = Modifier.styleModifier { property("gap", s(4)) }
                ) {
                    SpanText(
                        timeFrom,
                        modifier = Modifier.bodyMRegular().color(AppColors.Text.Secondary)
                            .styleModifier { property("white-space", "nowrap") }
                    )
                    SpanText(
                        "-",
                        modifier = Modifier.bodyMRegular().color(AppColors.Text.Secondary)
                    )
                    SpanText(
                        timeTo,
                        modifier = Modifier.bodyMRegular().color(AppColors.Text.Secondary)
                            .styleModifier { property("white-space", "nowrap") }
                    )
                }
            }

            // Vertical divider
            Box(
                Modifier
                    .sWidth(1)
                    .sHeight(12)
                    .styleModifier {
                        property("background-color", "rgba(255, 255, 255, 0.2)")
                    }
            )

            // Price
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.styleModifier { property("gap", s(2)) }
            ) {
                Image(
                    src = AppIcons.ModalDollar,
                    modifier = Modifier.sSize(14),
                    alt = t.modalPriceAlt
                )
                SpanText(
                    price,
                    modifier = Modifier.bodyMRegular().color(AppColors.Text.Secondary)
                        .styleModifier { property("white-space", "nowrap") }
                )
            }
        }
    }
}
